import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { config } from '../../config/environment';

const Complete: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [completing, setCompleting] = useState(true);
  const [tenantUrl, setTenantUrl] = useState('');
  const [error, setError] = useState('');

  const completeOnboarding = useCallback(async () => {
    try {
      // Check for flow ID in URL first, then localStorage
      const flowId = searchParams.get('flow') || searchParams.get('session') || searchParams.get('flowId');
      let onboardingFlowId = flowId || localStorage.getItem('onboardingFlowId');

      // If flow ID from URL, update localStorage
      if (flowId) {
        console.log('🔄 Complete - Flow ID from URL:', flowId);
        localStorage.setItem('onboardingFlowId', flowId);
        onboardingFlowId = flowId;
      }

      if (!onboardingFlowId) {
        setError('No onboarding flow found');
        setCompleting(false);
        return;
      }

      // Fetch onboarding flow details to get organization info
      const response = await fetch(config.api.onboardingFlowById(onboardingFlowId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const onboardingFlow = await response.json();

        // Check if the flow is complete and has organization details
        if (onboardingFlow.status === 'COMPLETE' && onboardingFlow.org) {
          setTenantUrl(`https://${onboardingFlow.org.subdomain}.mentorgain.com`);

          // Clear local storage
          localStorage.removeItem('leadData');
          localStorage.removeItem('selectedPlan');
          localStorage.removeItem('onboardingFlowId');
          localStorage.removeItem('initialLeadData');
          localStorage.removeItem('preSelectedPlanId');
        } else {
          setError('Onboarding is not yet complete. Please finish the setup process first.');
        }
      } else {
        setError('Failed to fetch onboarding details');
      }
    } catch (error) {
      console.error('Error fetching onboarding details:', error);
      setError('Something went wrong while loading your organization details');
    } finally {
      setCompleting(false);
    }
  }, [searchParams]);

  useEffect(() => {
    completeOnboarding();
  }, [completeOnboarding]);

  const redirectToTenant = () => {
    if (tenantUrl) {
      window.location.href = tenantUrl;
    }
  };

  if (completing) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs="12" md="6" className="text-center">
            <Spinner color="primary" size="lg" />
            <h5 className="mt-4">Finalizing your setup...</h5>
            <p className="text-muted">
              We're preparing your Mentorgain platform. This will just take a moment.
            </p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs="12" md="6" className="text-center">
            <div className="mb-4">
              <div 
                style={{
                  width: '100px',
                  height: '100px',
                  background: '#dc3545',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontSize: '3rem',
                  color: 'white'
                }}
              >
                ⚠️
              </div>
            </div>
            <h4 className="mb-3">Setup Error</h4>
            <p className="text-muted mb-4">{error}</p>
            <Button
              color="primary"
              onClick={() => {
                // Clear all onboarding-related localStorage
                localStorage.removeItem('onboardingFlowId');
                localStorage.removeItem('leadData');
                localStorage.removeItem('selectedPlan');
                localStorage.removeItem('initialLeadData');
                localStorage.removeItem('preSelectedPlanId');
                // Redirect to getting started
                window.location.href = '/getting-started';
              }}
            >
              Start Over
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-9 col-xl-8">
            <div className="text-center mb-5">
              <div className="mb-4">
                <div
                  style={{
                    width: '100px',
                    height: '100px',
                    background: 'linear-gradient(135deg, #48bb78, #38a169)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    fontSize: '3.5rem',
                    color: 'white'
                  }}
                >
                  🎉
                </div>
              </div>
              <h2 className="mb-3">Welcome to Mentorgain!</h2>
              <p className="text-muted lead mb-0">
                Your organization has been successfully set up and is ready to transform
                your mentorship programs.
              </p>
            </div>

            {/* Email Invite Notification */}
            <Alert color="info" className="mb-4 border-0 shadow-sm">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="mdi mdi-email-outline me-2" style={{ fontSize: '1.5rem' }}></i>
                <strong>Check Your Email!</strong>
              </div>
              <p className="mb-0 text-center">
                We've sent an admin invitation to your email address. Please check your inbox
                (and spam folder) to complete your admin account setup and access your platform.
              </p>
            </Alert>

            {tenantUrl && (
              <Card className="mb-4 border-0 shadow-sm">
                <CardBody className="text-center p-4">
                  <h5 className="text-primary mb-3">Your Platform is Ready!</h5>
                  <p className="mb-3">Access your personalized Mentorgain platform at:</p>
                  <div className="mb-4 p-3 bg-light rounded">
                    <code className="h6 text-dark">{tenantUrl}</code>
                  </div>
                  <Button
                    color="primary"
                    size="lg"
                    onClick={redirectToTenant}
                    className="px-5"
                  >
                    Launch Your Platform →
                  </Button>
                </CardBody>
              </Card>
            )}

            <Row className="g-3">
              <Col xs="12" md="4">
                <Card className="h-100 border-0 shadow-sm">
                  <CardBody className="text-center py-4">
                    <div className="mb-3">
                      <i className="mdi mdi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h6 className="mb-2">Payment Complete</h6>
                    <small className="text-muted">
                      Your subscription is active and ready
                    </small>
                  </CardBody>
                </Card>
              </Col>
              <Col xs="12" md="4">
                <Card className="h-100 border-0 shadow-sm">
                  <CardBody className="text-center py-4">
                    <div className="mb-3">
                      <i className="mdi mdi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h6 className="mb-2">Organization Created</h6>
                    <small className="text-muted">
                      Your workspace has been configured
                    </small>
                  </CardBody>
                </Card>
              </Col>
              <Col xs="12" md="4">
                <Card className="h-100 border-0 shadow-sm">
                  <CardBody className="text-center py-4">
                    <div className="mb-3">
                      <i className="mdi mdi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h6 className="mb-2">Platform Ready</h6>
                    <small className="text-muted">
                      Start building mentorship connections
                    </small>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <div className="text-center mt-5 pt-3 border-top">
              <p className="text-muted mb-0">
                Need help getting started? Our support team is here to help at{' '}
                <a href="mailto:support@mentorgain.com" className="text-decoration-none">support@mentorgain.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Complete;